import { fetchCancelReasonList } from '@/apis/order/order.api';
import { ICancelReasonListRes } from '@/apis/order/order.interface';
import VectorClose from '@/assets/icons/VectorClose.svg';
import UKMaskLoading from '@/components/UKMaskLoading/index';
import UKToast from '@/components/UKToast/index';
import { useTranslation } from '@/i18n/client';
import { FormControl, FormControlLabel, Modal, Radio, RadioGroup } from '@mui/material';
import to from 'await-to-js';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import CommonButton from '../commonButton';
import styles from './index.module.scss';
// 埋点
import UKPageError from '@/components/UKPageError';
import { EXPOSURE_RECORD } from '@/tracks/25344';

interface selectReasonModalProps {
  orderId: string;
  confirmHandler: (id: string, desc?: string) => void;
  closeHandler: () => void;
  trackHandler?: (extraParams: { cancelreason: string }) => void;
  commonTrackParams?: {
    orderId: string;
    orderStatus: string;
  };
}

const selectReasonModal: React.FC<selectReasonModalProps> = observer(
  ({ orderId, confirmHandler, closeHandler, trackHandler, commonTrackParams }) => {
    const { t } = useTranslation();
    const [cancelList, setCancelList] = useState<ICancelReasonListRes | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [reasonId, setReasonId] = useState('');

    const loadCancelList = async () => {
      UKMaskLoading.show();
      const [err, cancelListData] = await to(fetchCancelReasonList({ orderId }));

      if (err) {
        setError('Failed to fetch order details');
        setCancelList(null);
      } else {
        setCancelList(cancelListData as ICancelReasonListRes);
        setError(null);
      }
      UKMaskLoading.close();
      setLoading(false);
    };

    useEffect(() => {
      loadCancelList();
    }, []);

    // 选择原因
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const id = (event.target as HTMLInputElement).value;
      // 埋点需要的数据
      const desc = cancelList?.cancelReasonItemDTOList?.find(
        (item) => String(item?.refundReasonId) === id,
      )?.refundReasonDesc;
      // 埋点
      trackHandler && trackHandler({ cancelreason: desc || '' });
      setReasonId(id);
    };

    const handleConfirmClick = () => {
      if (!reasonId) {
        UKToast.show({
          content: t('order.orderList.selectReasonModal.errorTips'), // 弹窗内容
          icon: 'warning', // 可选：success, fail, loading, warn, info，影响图标
          duration: 2500, // 不写的话默认3000
        });
        return;
      }
      // 埋点需要数据
      const desc = cancelList?.cancelReasonItemDTOList?.find(
        (item) => String(item?.refundReasonId) === reasonId,
      )?.refundReasonDesc;
      confirmHandler && confirmHandler(reasonId, desc || '');
    };

    const { cancelReasonItemDTOList, skipSelectReason } = cancelList || {};
    // Shipped状态下取消订单，且大于5英磅直接弹拒收或者走售后的弹窗，无需选择原因
    if (skipSelectReason) {
      confirmHandler && confirmHandler('611');
      return;
    }

    return (
      !loading && (
        <Modal
          open={true}
          onClose={closeHandler}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <div className={styles.modal_content}>
            <div className={styles.modal_title}>
              <span>{t('order.orderList.selectReasonModal.modalTitle')}</span>
              <VectorClose className={styles.close_ico} onClick={closeHandler} />
            </div>
            {error ? ( // 异常兜底处理
              <UKPageError
                style={{ padding: '60px 0' }}
                text={t('order.orderList.errorPage.tip')}
                btnText={t('order.orderList.errorPage.btnTip')}
                clickTryAgain={() => {
                  window.location.reload();
                }}
              />
            ) : (
              <div
                className={styles.main_cont}
                exptag={'exp|' + EXPOSURE_RECORD.OrderDetail_CancelLayerExpo}
                data-exptag-config={JSON.stringify({
                  useClick: false,
                  stay_duration: 0.3,
                  repeated: false,
                  area_rate: 0.5,
                })}
                data-exptag-json-param={JSON.stringify(commonTrackParams)}
              >
                <FormControl>
                  <RadioGroup
                    aria-labelledby='order-radio-buttons-group-label'
                    name='radio-buttons-group'
                    onChange={handleChange}
                  >
                    {cancelReasonItemDTOList?.length
                      ? cancelReasonItemDTOList.map((item) => (
                          <FormControlLabel
                            key={item.refundReasonId}
                            value={item.refundReasonId}
                            control={
                              <Radio
                                disableRipple
                                size='small'
                                sx={{
                                  color: '#e0e1e5', // 设置Radio的颜色为绿色
                                }}
                              />
                            }
                            label={item.refundReasonDesc}
                          />
                        ))
                      : null}
                  </RadioGroup>
                </FormControl>
              </div>
            )}
            <div className={styles.oper_wrap}>
              <CommonButton variant='contained' onClick={handleConfirmClick} disabled={!reasonId} sx={{ boxShadow: 'none' }}>
                {t('order.orderList.common.submitBtnText')}
              </CommonButton>
            </div>
          </div>
        </Modal>
      )
    );
  },
);

export default selectReasonModal;
